'use client';

import { Suspense, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import { useRequireAuth } from '@/lib/useRequireAuth';
import EventPing from '@/components/EventPing';
import { translate } from '@/lib/i18n';
import { formatVolume } from '@/lib/format';
import ResearchTabs from '@/components/ResearchTabs';
import SectorRotation from '@/components/SectorRotation';

interface SpotRow {
  symbol: string;
  base: string;
  quote: string;
  exchange: string;
  product: 'spot';
  price: number | null;
  quoteVolume24h: number;
  returnPct: number;
  relativePp: number;
}

interface SpotResponse {
  window: string;
  benchmark: string;
  benchmarkReturnPct: number;
  asOf: number;
  mode: 'asset' | 'market';
  quoteFilter: string | null;
  windowStart: number | null;
  windowEnd: number | null;
  summary: { total: number; valid: number; coverage: number; breadthPct: number; reliable: boolean };
  rows: SpotRow[];
}

const WINDOWS = ['1D', '7D', '30D'] as const;

export default function ResearchSpotPage() {
  const authReady = useRequireAuth('/research/spot', true);
    return (
    <Suspense fallback={<div className="p-8 text-center text-gf-muted">…</div>}>
      <EventPing event="research_start" />
      <SpotInner />
    </Suspense>
  );
}

function SpotInner() {
  const { language } = useAppStore();
  const t = translate(language);
  const zh = language !== 'en';
  const searchParams = useSearchParams();

  const [window, setWindow] = useState<'1D' | '7D' | '30D'>(() => {
    const w = searchParams.get('window');
    return w === '1D' || w === '30D' ? w : '7D';
  });
  const [benchmark, setBenchmark] = useState<'BTC' | 'ETH'>(() => (searchParams.get('benchmark') === 'ETH' ? 'ETH' : 'BTC'));
  const [limit, setLimit] = useState(60);
  const [mode, setMode] = useState<'asset' | 'market'>(() => (searchParams.get('mode') === 'market' ? 'market' : 'asset'));

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['research-spot', window, benchmark, limit, mode],
    queryFn: () =>
      api.get<SpotResponse>(`/research/spot?window=${window.toLowerCase()}&benchmark=${benchmark}&limit=${limit}&mode=${mode}`),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const upCount = useMemo(() => data?.rows.filter((r) => r.returnPct > 0).length ?? 0, [data]);

  const winLabel = (w: string) => (w === '1D' ? '1D' : w === '30D' ? '30D' : '7D');

  const rangeText = data?.windowStart && data?.windowEnd
    ? `${new Date(data.windowStart).toLocaleDateString()} — ${new Date(data.windowEnd).toLocaleDateString()}`
    : '';

  return (
    <div className="mx-auto max-w-[1200px] px-3 py-4 sm:px-4">
      <ResearchTabs />
      {/* Title + controls */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold sm:text-2xl">{zh ? '现货相对强弱' : 'Spot Relative Strength'}</h1>
          <p className="mt-0.5 text-xs text-gf-muted">
            {zh ? '区间收益与基准（BTC/ETH）的相对差，单位百分点（pp）' : 'Period return vs benchmark (BTC/ETH), in percentage points (pp)'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-gf-border bg-gf-surface2 p-0.5">
            {(['asset', 'market'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`rounded-md px-3 py-1.5 text-xs transition-colors ${
                  mode === m ? 'bg-gf-accent text-white' : 'text-gf-muted hover:text-gf-text'
                }`}
              >
                {m === 'asset' ? (zh ? '按资产' : 'By asset') : zh ? '按市场' : 'By market'}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-gf-border bg-gf-surface2 p-0.5">
            {WINDOWS.map((w) => (
              <button
                key={w}
                onClick={() => setWindow(w)}
                className={`rounded-md px-3 py-1.5 text-xs transition-colors ${
                  window === w ? 'bg-gf-accent text-white' : 'text-gf-muted hover:text-gf-text'
                }`}
              >
                {w}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-gf-border bg-gf-surface2 p-0.5">
            {(['BTC', 'ETH'] as const).map((b) => (
              <button
                key={b}
                onClick={() => setBenchmark(b)}
                className={`rounded-md px-3 py-1.5 text-xs transition-colors ${
                  benchmark === b ? 'bg-gf-accent text-white' : 'text-gf-muted hover:text-gf-text'
                }`}
              >
                {b}
              </button>
            ))}
          </div>
          <button
            onClick={() => setLimit(limit === 60 ? 120 : 60)}
            className="rounded-lg border border-gf-border bg-gf-surface2 px-3 py-1.5 text-xs text-gf-muted transition-colors hover:text-gf-text"
          >
            {zh ? `前 ${limit} 名` : `Top ${limit}`}
          </button>
          <button onClick={() => refetch()} className="rounded-lg border border-gf-border bg-gf-surface2 px-3 py-1.5 text-xs text-gf-muted transition-colors hover:text-gf-text">
            {isFetching ? (zh ? '载入中…' : 'Loading…') : zh ? '刷新' : 'Refresh'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-gf-muted">{t.common.loading}</div>
      ) : !data ? (
        <div className="card p-8 text-center text-sm text-gf-muted">{zh ? '暂无数据' : 'No data'}</div>
      ) : (
        <>
          {/* Summary strip */}
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <SummaryCard
              label={zh ? `基准 ${data.benchmark} 收益` : `${data.benchmark} return`}
              value={`${data.benchmarkReturnPct >= 0 ? '+' : ''}${data.benchmarkReturnPct.toFixed(2)}%`}
              tone={data.benchmarkReturnPct >= 0 ? 'pos' : 'neg'}
            />
            <SummaryCard
              label={zh ? '上涨成员' : 'Advancing'}
              value={`${upCount} / ${data.rows.length}`}
              tone="pos"
            />
            <SummaryCard
              label={zh ? '市场宽度' : 'Breadth'}
              value={`${data.summary.breadthPct.toFixed(1)}%`}
              tone={data.summary.breadthPct >= 50 ? 'pos' : 'neg'}
            />
            <SummaryCard
              label={zh ? '覆盖率' : 'Coverage'}
              value={`${(data.summary.coverage * 100).toFixed(0)}%`}
              tone={data.summary.reliable ? 'pos' : 'warn'}
              sub={!data.summary.reliable ? (zh ? '低于品质门槛' : 'Below quality gate') : undefined}
            />
          </div>

          {/* Leader board */}
          <div className="card !p-0 overflow-hidden">
            <div className="border-b border-gf-border px-4 py-2.5 text-sm font-semibold">
              {zh
                ? `相对强弱排行（${winLabel(data.window)} vs ${data.benchmark}）${mode === 'asset' ? ' · 按资产（统一 USDT 报价）' : ' · 按市场'}`
                : `Relative strength (${winLabel(data.window)} vs ${data.benchmark})${mode === 'asset' ? ' · by asset (USDT-quoted only)' : ' · by market'}`}
            </div>
            <div className="overflow-x-auto">
              <table className="table-base">
                <thead className="bg-gf-surface2/60">
                  <tr>
                    <th className="w-10">#</th>
                    <th>{zh ? '币种' : 'Symbol'}</th>
                    {mode === 'market' && <th>{zh ? '报价币' : 'Quote'}</th>}
                    <th className="text-right">{zh ? '价格' : 'Price'}</th>
                    <th className="text-right">{zh ? '24H 成交额' : '24h Volume'}</th>
                    <th className="text-right">{zh ? '区间收益' : 'Return'}</th>
                    <th className="text-right">{zh ? '相对差 (pp)' : 'Relative (pp)'}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={r.symbol} className="cursor-pointer transition-colors hover:bg-gf-surface2/40" onClick={() => { globalThis.location.href = `/coin/${r.symbol}?type=spot&cb=rs${data.window}`; }}>
                      <td className="text-xs text-gf-muted">{i + 1}</td>
                      <td className="font-medium">{r.symbol}</td>
                      {mode === 'market' && <td className="text-xs text-gf-muted">{r.quote}</td>}
                      <td className="text-right font-mono text-xs">{r.price != null ? (r.price >= 1000 ? r.price.toLocaleString('en-US', { maximumFractionDigits: 0 }) : Number(r.price.toFixed(4))) : '—'}</td>
                      <td className="text-right text-xs text-gf-muted">{formatVolume(r.quoteVolume24h)}</td>
                      <td className={`text-right font-mono text-xs ${r.returnPct >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>
                        {r.returnPct >= 0 ? '+' : ''}{r.returnPct.toFixed(2)}%
                      </td>
                      <td className={`text-right font-mono text-xs ${r.relativePp >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>
                        {r.relativePp >= 0 ? '+' : ''}{r.relativePp.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  {data.rows.length === 0 && (
                    <tr>
                      <td colSpan={mode === 'market' ? 7 : 6} className="py-8 text-center text-gf-muted">{zh ? '暂无有效数据' : 'No valid data'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="mt-3 text-[11px] text-gf-muted">
            {zh
              ? `区间收益 = 期末收盘 / 期初收盘 − 1；相对差 = 100 × (币种收益 − 基准收益)，单位 pp。${mode === 'asset' ? '按资产模式：同资产多报价仅保留成交额最高的 USDT 市场，避免 NEARUSDT 与 NEARTRY 混算；' : '按市场模式：保留各报价币市场（USDT/USDC/TRY 等），收益仅在同报价币内比较。'}排除稳定币与杠杆代币；数据源 Binance 现货日 K。${rangeText ? `范围 ${rangeText}。` : ''}更新时间 ${new Date(data.asOf).toLocaleTimeString()}`
              : `Return = close_end / close_start − 1; relative = 100 × (asset − benchmark) in pp. ${mode === 'asset' ? 'Asset mode: dedupe by base asset, USDT-quoted markets only; ' : 'Market mode: per-quote markets kept, returns compared within the same quote. '}Stablecoins & leveraged tokens excluded; source Binance spot daily klines. ${rangeText ? `Range ${rangeText}. ` : ''}Updated ${new Date(data.asOf).toLocaleTimeString()}`}
          </p>

          {/* R02 sector rotation */}
          <div className="mt-4">
            <SectorRotation window={winLabel(data.window)} />
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone, sub }: { label: string; value: string; tone: 'pos' | 'neg' | 'warn'; sub?: string }) {
  const toneClass = tone === 'pos' ? 'text-gf-positive' : tone === 'neg' ? 'text-gf-negative' : 'text-gf-warn';
  return (
    <div className="card !p-3">
      <div className="text-[11px] text-gf-muted">{label}</div>
      <div className={`mt-0.5 text-base font-semibold sm:text-lg ${toneClass}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[10px] text-gf-warn">{sub}</div>}
    </div>
  );
}
